import { useState, useEffect } from "react";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Bookmark, 
  Plus,
  Search,
  Trash2,
  Edit,
  MoreVertical,
  Lightbulb,
  Hash,
  TrendingUp,
  FileText,
  Loader2,
  ArrowLeft,
  CheckCircle,
  Clock,
  Tag,
  Instagram,
  Twitter,
  Facebook,
  Linkedin,
  Youtube,
  Filter
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CollectionsPage = ({ user, logout, token }) => {
  const [ideas, setIdeas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [ideaForm, setIdeaForm] = useState({
    title: "",
    content: "",
    category: "general",
    tags: [],
    platform: ""
  });
  const [tagInput, setTagInput] = useState("");

  const categories = [
    { id: "general", name: "General", icon: <Lightbulb className="w-4 h-4" />, color: "bg-gray-500" },
    { id: "content", name: "Ideas de Contenido", icon: <FileText className="w-4 h-4" />, color: "bg-blue-500" },
    { id: "hashtags", name: "Hashtags", icon: <Hash className="w-4 h-4" />, color: "bg-cyan-500" },
    { id: "trends", name: "Tendencias", icon: <TrendingUp className="w-4 h-4" />, color: "bg-purple-500" }
  ];

  const statuses = [
    { id: "saved", name: "Guardada", icon: <Bookmark className="w-4 h-4" />, color: "text-blue-500" },
    { id: "in_progress", name: "En Progreso", icon: <Clock className="w-4 h-4" />, color: "text-orange-500" },
    { id: "used", name: "Usada", icon: <CheckCircle className="w-4 h-4" />, color: "text-green-500" }
  ];

  const platformIcons = {
    instagram: <Instagram className="w-4 h-4" />,
    twitter: <Twitter className="w-4 h-4" />,
    facebook: <Facebook className="w-4 h-4" />,
    linkedin: <Linkedin className="w-4 h-4" />,
    youtube: <Youtube className="w-4 h-4" />,
    tiktok: <span className="text-xs font-bold">TT</span>
  };

  const platforms = [
    { id: "instagram", name: "Instagram" },
    { id: "twitter", name: "Twitter/X" },
    { id: "tiktok", name: "TikTok" },
    { id: "linkedin", name: "LinkedIn" },
    { id: "facebook", name: "Facebook" },
    { id: "youtube", name: "YouTube" }
  ];

  useEffect(() => {
    fetchIdeas();
    fetchStats();
  }, [token, searchQuery, filterCategory, filterStatus]);

  const fetchIdeas = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (filterCategory && filterCategory !== "all") params.append("category", filterCategory);
      if (filterStatus && filterStatus !== "all_status") params.append("status", filterStatus);

      const response = await axios.get(`${BACKEND_URL}/api/trends/ideas?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdeas(response.data);
    } catch (error) {
      console.error("Error fetching ideas:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trends/ideas/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleSaveIdea = async () => {
    if (!ideaForm.title.trim()) {
      toast.error("El título es requerido");
      return;
    }

    setSaving(true);
    
    // Clean up platform if "none" selected
    const dataToSend = {
      ...ideaForm,
      platform: ideaForm.platform === "none" ? null : ideaForm.platform
    };

    try {
      if (editingIdea) {
        await axios.put(
          `${BACKEND_URL}/api/trends/ideas/${editingIdea.idea_id}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Idea actualizada");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/trends/ideas`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Idea guardada");
      }

      setDialogOpen(false);
      resetForm();
      fetchIdeas();
      fetchStats();
    } catch (error) {
      toast.error("Error al guardar");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/trends/ideas/${ideaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Idea eliminada");
      fetchIdeas();
      fetchStats();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleUpdateStatus = async (ideaId, newStatus) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/trends/ideas/${ideaId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Estado actualizado");
      fetchIdeas();
      fetchStats();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const resetForm = () => {
    setIdeaForm({
      title: "",
      content: "",
      category: "general",
      tags: [],
      platform: ""
    });
    setTagInput("");
    setEditingIdea(null);
  };

  const openEditDialog = (idea) => {
    setEditingIdea(idea);
    setIdeaForm({
      title: idea.title,
      content: idea.content || "",
      category: idea.category,
      tags: idea.tags || [],
      platform: idea.platform || ""
    });
    setDialogOpen(true);
  };

  const addTag = () => {
    if (tagInput.trim() && !ideaForm.tags.includes(tagInput.trim())) {
      setIdeaForm({ ...ideaForm, tags: [...ideaForm.tags, tagInput.trim()] });
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setIdeaForm({ ...ideaForm, tags: ideaForm.tags.filter(t => t !== tagToRemove) });
  };

  const getCategoryInfo = (categoryId) => categories.find(c => c.id === categoryId) || categories[0];
  const getStatusInfo = (statusId) => statuses.find(s => s.id === statusId) || statuses[0];

  return (
    <div className="min-h-screen bg-background">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/ideas" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a Ideas
            </Link>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500/20 to-yellow-500/20">
                    <Bookmark className="w-6 h-6 text-orange-500" />
                  </div>
                  <h1 className="text-3xl font-bold" data-testid="collections-page-title">Colecciones</h1>
                </div>
                <p className="text-muted-foreground">
                  Guarda y organiza tus ideas de contenido
                </p>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="gradient-neural glow-neural text-white" data-testid="add-idea-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Idea
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {editingIdea ? "Editar Idea" : "Nueva Idea"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Título</Label>
                      <Input
                        placeholder="Título de tu idea..."
                        value={ideaForm.title}
                        onChange={(e) => setIdeaForm({ ...ideaForm, title: e.target.value })}
                        data-testid="idea-title-input"
                      />
                    </div>

                    <div>
                      <Label>Descripción (opcional)</Label>
                      <Textarea
                        placeholder="Describe tu idea con más detalle..."
                        value={ideaForm.content}
                        onChange={(e) => setIdeaForm({ ...ideaForm, content: e.target.value })}
                        className="min-h-[100px]"
                        data-testid="idea-content-textarea"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Categoría</Label>
                        <Select
                          value={ideaForm.category}
                          onValueChange={(v) => setIdeaForm({ ...ideaForm, category: v })}
                        >
                          <SelectTrigger data-testid="idea-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                <div className="flex items-center gap-2">
                                  {c.icon}
                                  {c.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Plataforma (opcional)</Label>
                        <Select
                          value={ideaForm.platform}
                          onValueChange={(v) => setIdeaForm({ ...ideaForm, platform: v })}
                        >
                          <SelectTrigger data-testid="idea-platform-select">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Ninguna</SelectItem>
                            {platforms.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                <div className="flex items-center gap-2">
                                  {platformIcons[p.id]}
                                  {p.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Tags</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Añadir tag..."
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                        />
                        <Button type="button" variant="outline" onClick={addTag}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {ideaForm.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {ideaForm.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="cursor-pointer"
                              onClick={() => removeTag(tag)}
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                              <span className="ml-1 text-muted-foreground">x</span>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button
                      className="w-full"
                      onClick={handleSaveIdea}
                      disabled={saving}
                      data-testid="save-idea-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Bookmark className="w-4 h-4 mr-2" />
                          {editingIdea ? "Actualizar" : "Guardar Idea"}
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total</p>
                      <p className="text-2xl font-bold">{stats.total}</p>
                    </div>
                    <Lightbulb className="w-8 h-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Guardadas</p>
                      <p className="text-2xl font-bold text-blue-500">{stats.by_status?.saved || 0}</p>
                    </div>
                    <Bookmark className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">En Progreso</p>
                      <p className="text-2xl font-bold text-orange-500">{stats.by_status?.in_progress || 0}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Usadas</p>
                      <p className="text-2xl font-bold text-green-500">{stats.by_status?.used || 0}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar ideas..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-ideas-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-[160px]" data-testid="filter-category">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[140px]" data-testid="filter-status">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_status">Todos</SelectItem>
                      {statuses.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ideas Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : ideas.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">No hay ideas guardadas</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || (filterCategory && filterCategory !== "all") || (filterStatus && filterStatus !== "all_status")
                    ? "No se encontraron ideas con esos filtros"
                    : "Comienza guardando tu primera idea de contenido"
                  }
                </p>
                {!searchQuery && (!filterCategory || filterCategory === "all") && (!filterStatus || filterStatus === "all_status") && (
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Idea
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ideas.map((idea) => {
                const category = getCategoryInfo(idea.category);
                const status = getStatusInfo(idea.status);

                return (
                  <Card key={idea.idea_id} className="hover:shadow-md transition-shadow" data-testid={`idea-${idea.idea_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`p-1.5 rounded text-white ${category.color}`}>
                            {category.icon}
                          </span>
                          <Badge variant="outline" className={status.color}>
                            {status.icon}
                            <span className="ml-1">{status.name}</span>
                          </Badge>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(idea)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {statuses.filter(s => s.id !== idea.status).map((s) => (
                              <DropdownMenuItem
                                key={s.id}
                                onClick={() => handleUpdateStatus(idea.idea_id, s.id)}
                              >
                                {s.icon}
                                <span className="ml-2">Marcar como {s.name}</span>
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteIdea(idea.idea_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <CardTitle className="text-lg mt-2">{idea.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {idea.content && (
                        <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                          {idea.content}
                        </p>
                      )}
                      
                      {idea.platform && (
                        <div className="flex items-center gap-1 mb-3">
                          {platformIcons[idea.platform]}
                          <span className="text-xs text-muted-foreground">
                            {platforms.find(p => p.id === idea.platform)?.name}
                          </span>
                        </div>
                      )}

                      {idea.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {idea.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {idea.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{idea.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground">
                        {format(new Date(idea.created_at), "PPP", { locale: es })}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CollectionsPage;
